import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit2, Trash2, Save, X, Clock, AlertCircle } from 'lucide-react';

const SpecialDaysUI = () => {
  const [specialDays, setSpecialDays] = useState([]);
  const [editingDay, setEditingDay] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [formData, setFormData] = useState({
    restaurantId: '',
    date: '',
    name: '',
    openTime: '',
    closeTime: '',
    isClosed: false,
    note: ''
  });

  // Load special days from API
  useEffect(() => {
    fetchSpecialDays();
  }, []);

  const fetchSpecialDays = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/SpecialDays`);
      if (response.ok) {
        const data = await response.json();
        setSpecialDays(data);
      } else {
        setError('Özel günler yüklenirken hata oluştu');
      }
    } catch (err) {
      setError('API bağlantı hatası');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      restaurantId: '',
      date: '',
      name: '',
      openTime: '',
      closeTime: '',
      isClosed: false,
      note: ''
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    resetForm();
  };

  const handleEdit = (day) => {
    setEditingDay(day.specialDayId);
    setFormData({
      restaurantId: day.restaurantId,
      date: day.date,
      name: day.name || '',
      openTime: day.openTime ? day.openTime.substring(0, 5) : '',
      closeTime: day.closeTime ? day.closeTime.substring(0, 5) : '',
      isClosed: day.isClosed,
      note: day.note || ''
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formData.restaurantId) {
        setError('Restoran seçimi zorunludur');
        return;
      }

      if (!formData.date) {
        setError('Tarih seçimi zorunludur');
        return;
      }

      if (!formData.isClosed && (!formData.openTime || !formData.closeTime)) {
        setError('Açılış ve kapanış saatleri zorunludur');
        return;
      }

      if (!formData.isClosed && formData.openTime >= formData.closeTime) {
        setError('Kapanış saati açılış saatinden sonra olmalıdır');
        return;
      }

      // Check if date already exists for this restaurant
      const existingDay = specialDays.find(d => 
        d.restaurantId === formData.restaurantId && 
        d.date === formData.date &&
        d.specialDayId !== editingDay
      );

      if (existingDay) {
        setError('Bu tarih için zaten özel gün tanımlanmış');
        return;
      }

      const requestData = {
        restaurantId: formData.restaurantId,
        date: formData.date,
        name: formData.name || null,
        openTime: formData.isClosed ? null : formData.openTime + ':00',
        closeTime: formData.isClosed ? null : formData.closeTime + ':00',
        isClosed: formData.isClosed,
        note: formData.note || null
      };

      let response;
      
      if (editingDay) {
        // Update existing day
        response = await fetch(`${API_BASE_URL}/api/SpecialDays/${editingDay}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
      } else {
        // Create new day
        response = await fetch(`${API_BASE_URL}/api/SpecialDays`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
      }

      if (response.ok) {
        // Refresh the list
        await fetchSpecialDays();
        setIsCreating(false);
        setEditingDay(null);
        resetForm();
      } else {
        const errorData = await response.text();
        setError(errorData || 'İşlem sırasında hata oluştu');
      }
    } catch (err) {
      setError('API bağlantı hatası');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingDay(null);
    resetForm();
    setError('');
  };

  const handleDelete = async (dayId) => {
    if (window.confirm('Bu özel günü silmek istediğinizden emin misiniz?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/SpecialDays/${dayId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          // Refresh the list
          await fetchSpecialDays();
        } else {
          setError('Silme işlemi sırasında hata oluştu');
        }
      } catch (err) {
        setError('API bağlantı hatası');
        console.error('API Error:', err);
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    return time.substring(0, 5); // HH:MM format
  };

  const isUpcoming = (dateString) => {
    const today = new Date();
    const specialDate = new Date(dateString);
    return specialDate >= today;
  };

  const isPast = (dateString) => {
    const today = new Date();
    const specialDate = new Date(dateString);
    return specialDate < today;
  };

  const sortedSpecialDays = [...specialDays].sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Özel Günler</h1>
                <p className="text-gray-600">Tatil günleri ve özel gün çalışma saatlerini yönetin</p>
              </div>
            </div>
            {!isCreating && !editingDay && (
              <button
                onClick={handleCreate}
                className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Yeni Özel Gün</span>
              </button>
            )}
          </div>
        </div>

        {/* Form */}
        {(isCreating || editingDay) && (
          <div className="border-b border-gray-200 p-6 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingDay ? 'Özel Günü Düzenle' : 'Yeni Özel Gün Ekle'}
            </h2>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Restoran ID
                </label>
                <input
                  type="text"
                  name="restaurantId"
                  value={formData.restaurantId}
                  onChange={handleInputChange}
                  placeholder="Restoran ID girin"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tarih
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Özel Gün Adı
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Örn: Yılbaşı, Kurban Bayramı"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div className="md:col-span-1">
                <div className="flex items-center space-x-3 mb-4">
                  <input
                    type="checkbox"
                    id="isClosed"
                    name="isClosed"
                    checked={formData.isClosed}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isClosed" className="text-sm font-medium text-gray-700">
                    Bu gün kapalı
                  </label>
                </div>
              </div>

              {!formData.isClosed && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Açılış Saati
                    </label>
                    <input
                      type="time"
                      name="openTime"
                      value={formData.openTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kapanış Saati
                    </label>
                    <input
                      type="time"
                      name="closeTime"
                      value={formData.closeTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Not
                </label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Özel gün hakkında not ekleyin..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-6">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Kaydediliyor...' : 'Kaydet'}</span>
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
                <span>İptal</span>
              </button>
            </div>
          </div>
        )}

        {/* Special Days List */}
        <div className="p-6">
          {loading && specialDays.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Yükleniyor...</p>
            </div>
          ) : sortedSpecialDays.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz özel gün tanımlanmamış</h3>
              <p className="text-gray-600 mb-4">İlk özel gününüzü ekleyerek başlayın</p>
              <button
                onClick={handleCreate}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Özel Gün Ekle
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedSpecialDays.map((day) => (
                <div 
                  key={day.specialDayId} 
                  className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${
                    isPast(day.date) ? 'border-gray-200 opacity-75' : 
                    isUpcoming(day.date) ? 'border-purple-200 bg-purple-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Calendar className={`h-4 w-4 ${isPast(day.date) ? 'text-gray-400' : 'text-purple-500'}`} />
                      <span className={`font-medium ${isPast(day.date) ? 'text-gray-600' : 'text-gray-900'}`}>
                        {formatDate(day.date)}
                      </span>
                      {isPast(day.date) && (
                        <span className="text-xs text-gray-500">(Geçmiş)</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleEdit(day)}
                        className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(day.specialDayId)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {day.name && (
                      <div className="font-medium text-purple-900">
                        {day.name}
                      </div>
                    )}
                    
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Restoran:</span> {day.restaurantId}
                    </div>
                    
                    {day.isClosed ? (
                      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Kapalı
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatTime(day.openTime)} - {formatTime(day.closeTime)}
                        </div>
                      </div>
                    )}

                    {day.note && (
                      <div className="text-xs text-gray-500 mt-2 p-2 bg-gray-50 rounded">
                        {day.note}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpecialDaysUI;